import type { AvailabilityBlock, UnitCalendar } from '../api/masters';
import type { BookingStage } from '../generated/enums';

/**
 * The month view's arithmetic, kept away from its markup.
 *
 * Everything here is dates and clipping — the two things a calendar is actually made of, and the
 * two things a screenshot cannot check. A bar that starts a day late looks entirely plausible.
 *
 * **Dates are `YYYY-MM-DD` strings end to end and every calculation goes through `Date.UTC`.** A
 * rental date is a calendar day at the shop (rule 14), so the local timezone must never touch it:
 * `new Date('2026-03-12')` west of Greenwich is the 11th at 19:00, and a grid built from that is
 * off by one for half the world. The same reason `daysBetween` counts by `Date.UTC` rather than by
 * dividing milliseconds, which a daylight-saving boundary makes fractional.
 */

/** Monday. The week starts here, by hand, for the reason the morning screen says: dayjs's
 * `startOf('week')` reads the locale, so it silently starts on Sunday for an `en-US` browser and
 * the same shop's two machines would draw different months. */
const WEEK_STARTS_ON = 1;

export function parseIso(iso: string): { year: number; month: number; day: number } {
  const [year, month, day] = iso.split('-').map(Number);

  return { year: year ?? 1970, month: month ?? 1, day: day ?? 1 };
}

export function toIso(utc: number): string {
  return new Date(utc).toISOString().slice(0, 10);
}

function utcOf(iso: string): number {
  const { year, month, day } = parseIso(iso);

  return Date.UTC(year, month - 1, day);
}

export function addDays(iso: string, days: number): string {
  const { year, month, day } = parseIso(iso);

  return toIso(Date.UTC(year, month - 1, day + days));
}

/** Inclusive at both ends, like every range in this product: 12 to 14 March is three days. */
export function daysBetween(from: string, to: string): number {
  return Math.round((utcOf(to) - utcOf(from)) / 86_400_000) + 1;
}

export function eachDay(from: string, to: string): string[] {
  const count = daysBetween(from, to);

  if (count <= 0) {
    return [];
  }

  return Array.from({ length: count }, (_, index) => addDays(from, index));
}

/** The overlap test the whole availability module is built on, restated for a client that must not
 * disagree with it: `a.From <= b.To AND b.From <= a.To`, inclusive both ends. */
export function overlaps(from: string, to: string, otherFrom: string, otherTo: string): boolean {
  return from <= otherTo && otherFrom <= to;
}

export interface DayCell {
  iso: string;
  /** Day of the month, for the number printed in the corner. */
  day: number;
  /** False for the leading and trailing days that fill the first and last weeks. */
  inMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
}

/**
 * The whole month laid out as Monday-first weeks, including the days either side that fill the
 * first and last rows.
 *
 * Those filler days are real dates rather than blanks, because a booking that starts on the 30th of
 * the previous month is exactly the one a shop needs to see when it opens March.
 */
export function monthGrid(anchorIso: string, todayIso: string): DayCell[][] {
  const { year, month } = parseIso(anchorIso);

  const first = Date.UTC(year, month - 1, 1);

  // getUTCDay is Sunday-0; shift so Monday is 0 and Sunday is 6.
  const lead = (new Date(first).getUTCDay() - WEEK_STARTS_ON + 7) % 7;

  const start = toIso(Date.UTC(year, month - 1, 1 - lead));

  const weeks: DayCell[][] = [];

  for (let week = 0; week < 6; week += 1) {
    const days: DayCell[] = [];

    for (let day = 0; day < 7; day += 1) {
      const iso = addDays(start, week * 7 + day);
      const parsed = parseIso(iso);
      const weekday = new Date(utcOf(iso)).getUTCDay();

      days.push({
        iso,
        day: parsed.day,
        inMonth: parsed.month === month && parsed.year === year,
        isToday: iso === todayIso,
        isWeekend: weekday === 0 || weekday === 6,
      });
    }

    weeks.push(days);

    // Six rows is the worst case, not the usual one. Stop as soon as the month is spent, or every
    // short month draws a whole empty week.
    const next = addDays(start, (week + 1) * 7);

    if (parseIso(next).month !== month || parseIso(next).year !== year) {
      break;
    }
  }

  return weeks;
}

/** The window the grid actually covers, filler days included — what to ask the server for. */
export function monthWindow(anchorIso: string, todayIso: string): { from: string; to: string } {
  const weeks = monthGrid(anchorIso, todayIso);
  const first = weeks[0]?.[0]?.iso ?? anchorIso;
  const lastWeek = weeks[weeks.length - 1];
  const last = lastWeek?.[lastWeek.length - 1]?.iso ?? anchorIso;

  return { from: first, to: last };
}

export function shiftMonth(anchorIso: string, months: number): string {
  const { year, month } = parseIso(anchorIso);

  return toIso(Date.UTC(year, month - 1 + months, 1));
}

export function startOfMonth(iso: string): string {
  const { year, month } = parseIso(iso);

  return toIso(Date.UTC(year, month - 1, 1));
}

/* ------------------------------------------------------------------ the bars */

/**
 * What a block means on the rail, named for the thing rather than for a colour.
 *
 * `danger` and `warning` are never identity in this product — they mean late and needs-attention
 * everywhere — so the page maps these to hues, and `tentative` is the one that deliberately earns
 * amber: a claim that can still lapse is precisely something somebody has to chase.
 */
export type BlockTone = 'booked' | 'tentative' | 'cleaning' | 'tailor' | 'other';

export function blockTone(block: AvailabilityBlock): BlockTone {
  switch (block.blockType) {
    // `isTentative` qualifies a booking and nothing else. It is set on the turnaround block behind a
    // hold as well — that block lapses with it — but reading it first painted the buffer amber too,
    // so a held garment drew as two amber bars with nothing to say the second was the cleaning. The
    // legend has no entry for a tentative turnaround, and inventing one is worse than colouring the
    // buffer for what it is.
    case 'Booking':
      return block.isTentative ? 'tentative' : 'booked';
    case 'Cleaning':
      return 'cleaning';
    case 'Alteration':
      return 'tailor';
    default:
      return 'other';
  }
}

/**
 * Whether a block still applies.
 *
 * Released and expired rows come down from the server on purpose — "why was this suit unavailable
 * last Tuesday" needs them — so the calendar has to be the thing that decides they no longer take
 * the garment off the rail. Drawing them as solid bars would show a shop a rail full of bookings it
 * has already let go.
 */
export function isLive(block: AvailabilityBlock): boolean {
  return !block.isReleased && !block.isExpired;
}

export interface Bar {
  block: AvailabilityBlock;
  tone: BlockTone;
  /** Column index of the first day the bar occupies, clipped to the window. */
  offset: number;
  /** How many day columns it spans, clipped to the window. */
  length: number;
  /** The block starts before the window — draw the leading edge open. */
  clippedStart: boolean;
  clippedEnd: boolean;
}

/**
 * Lays one garment's blocks onto the window's day columns.
 *
 * **Clipping is the whole job.** A March booking that runs into April must stop at the edge of the
 * page rather than drawing a bar past the last column, and one that started in February must still
 * appear on 1 March rather than being dropped for starting off-screen — that second case is the one
 * that silently tells a shop a garment is free when it is in somebody's car.
 */
export function barsFor(blocks: readonly AvailabilityBlock[], from: string, to: string): Bar[] {
  const bars: Bar[] = [];

  for (const block of blocks) {
    if (!isLive(block) || !overlaps(block.fromDate, block.toDate, from, to)) {
      continue;
    }

    const start = block.fromDate < from ? from : block.fromDate;
    const end = block.toDate > to ? to : block.toDate;

    bars.push({
      block,
      tone: blockTone(block),
      offset: daysBetween(from, start) - 1,
      length: daysBetween(start, end),
      clippedStart: block.fromDate < from,
      clippedEnd: block.toDate > to,
    });
  }

  return bars.sort((left, right) => left.offset - right.offset || left.block.id - right.block.id);
}

/** True when nothing takes this garment off the rail anywhere in the window. */
export function isFreeAllWindow(unit: UnitCalendar, from: string, to: string): boolean {
  return barsFor(unit.blocks, from, to).length === 0;
}

/* -------------------------------------------------------------- the schedule */

export interface ScheduledBooking {
  bookingId: number;
  documentNumber: string | null;
  customerName: string;
  stage: BookingStage;
  isTentative: boolean;
  /** How many garments this booking has on the rail — one row per booking, not per garment. */
  garments: number;
  /** First day any of its garments leaves, and the last day any comes back. */
  outOn: string;
  backOn: string;
}

/**
 * The bookings behind the window's blocks, one row each.
 *
 * A booking of four garments places four blocks; the schedule shows it once with a count, because a
 * day cell listing the same wedding four times is a cell nobody reads. The span is the union of its
 * garments' spans — if one piece goes out a day early for a fitting, the booking is out that day.
 */
export function bookingsInWindow(
  units: readonly UnitCalendar[],
  from: string,
  to: string,
): ScheduledBooking[] {
  const byBooking = new Map<number, ScheduledBooking>();

  for (const unit of units) {
    for (const block of unit.blocks) {
      const holder = block.heldBy;

      // Only the booking's own row names a booking. Its cleaning block shares the `refId` but is
      // the turnaround, and counting it would move every booking's "back on" into the buffer.
      if (
        !holder ||
        block.blockType !== 'Booking' ||
        !isLive(block) ||
        !overlaps(block.fromDate, block.toDate, from, to)
      ) {
        continue;
      }

      const existing = byBooking.get(holder.bookingId);

      if (existing) {
        existing.garments += 1;
        existing.outOn = existing.outOn < block.fromDate ? existing.outOn : block.fromDate;
        existing.backOn = existing.backOn > block.toDate ? existing.backOn : block.toDate;
        continue;
      }

      byBooking.set(holder.bookingId, {
        bookingId: holder.bookingId,
        documentNumber: holder.documentNumber,
        customerName: holder.customerName,
        stage: holder.stage,
        isTentative: block.isTentative,
        garments: 1,
        outOn: block.fromDate,
        backOn: block.toDate,
      });
    }
  }

  return [...byBooking.values()].sort(
    (left, right) => left.outOn.localeCompare(right.outOn) || left.bookingId - right.bookingId,
  );
}

export interface DaySchedule {
  /** Bookings whose garments leave the shop on this day. */
  out: ScheduledBooking[];
  /** Bookings whose garments are due back on this day. */
  back: ScheduledBooking[];
}

/**
 * What happens on one day.
 *
 * Out and back are kept apart rather than merged into "active", because those are the two things a
 * counter physically does on a given morning; a booking that merely spans the day needs nobody to
 * do anything and would bury the two that do.
 */
export function scheduleOn(iso: string, bookings: readonly ScheduledBooking[]): DaySchedule {
  return {
    out: bookings.filter((booking) => booking.outOn === iso),
    back: bookings.filter((booking) => booking.backOn === iso),
  };
}

/* ---------------------------------------------------- one garment's own month */

export interface DayMark {
  tone: BlockTone;
  block: AvailabilityBlock;
  /** The first day of this run that the window shows — where a band is capped and labelled. */
  isStart: boolean;
  isEnd: boolean;
}

/**
 * One garment's month, a day at a time — what a scanned tag turns the grid into.
 *
 * <p>
 * The whole-shop schedule answers "what happens today"; holding one garment, the question is the
 * other way round: <strong>"when is this piece free"</strong>, and the answer is the days it is
 * not. So the same grid stops listing bookings in the cells and starts marking the days this
 * garment is spoken for, in runs — 3rd to the 6th as one band, the 8th to the 10th as the next.
 * </p>
 *
 * <p>
 * <strong>At most one live block can cover a day</strong>, which is the availability engine's
 * entire promise, so a day gets one mark rather than a stack. Released and expired rows are skipped
 * for the same reason they are not drawn on the rail: they are history, not occupancy.
 * </p>
 *
 * <p>
 * `isStart` is the first day <em>the window shows</em>, not the block's own first day, so a booking
 * running in from last month is capped at the edge and labelled there rather than being labelled
 * off-screen and appearing to belong to nobody.
 * </p>
 */
export function unitMarks(
  blocks: readonly AvailabilityBlock[],
  from: string,
  to: string,
): Map<string, DayMark> {
  const marks = new Map<string, DayMark>();

  for (const bar of barsFor(blocks, from, to)) {
    const first = bar.block.fromDate < from ? from : bar.block.fromDate;
    const last = bar.block.toDate > to ? to : bar.block.toDate;

    for (const iso of eachDay(first, last)) {
      // First writer wins. Two live blocks over one day would be the double-booking the engine
      // exists to refuse, and drawing the second over the first would hide it rather than show it.
      if (marks.has(iso)) {
        continue;
      }

      marks.set(iso, {
        tone: bar.tone,
        block: bar.block,
        isStart: iso === first,
        isEnd: iso === last,
      });
    }
  }

  return marks;
}

/** The runs themselves, for a list that wants "3–6 Sep" rather than four separate days. */
export function unitRuns(blocks: readonly AvailabilityBlock[], from: string, to: string): Bar[] {
  return barsFor(blocks, from, to);
}

export interface AgendaDay extends DaySchedule {
  iso: string;
}

/**
 * The same month as a list, for a screen too narrow to be a grid.
 *
 * <strong>Only the days something happens on.</strong> A seven-column grid degrades on a phone to a
 * strip showing two and a half columns, which is the failure the list components already solved by
 * changing presentation rather than shrinking — so the narrow calendar answers the same question in
 * the shape a phone can hold: not "here is October", but "here is what you do, in order".
 *
 * It walks the grid's own window rather than the calendar month, so the two presentations never
 * disagree about whether a booking on the 30th of the previous month is in view.
 */
export function agenda(
  from: string,
  to: string,
  bookings: readonly ScheduledBooking[],
): AgendaDay[] {
  return eachDay(from, to)
    .map((iso) => ({ iso, ...scheduleOn(iso, bookings) }))
    .filter((day) => day.out.length > 0 || day.back.length > 0);
}

/**
 * What each tone is called, in one place.
 *
 * The legend and the narrow rail both name these, and a bar the legend calls one thing while the
 * list beside it calls another is worse than either name alone — which is exactly how the tentative
 * turnaround went unnoticed until somebody held a garment and looked at the rail.
 */
export const toneLabel: Record<BlockTone, string> = {
  booked: 'Booked',
  tentative: 'On hold — can still lapse',
  cleaning: 'Cleaning / turnaround',
  tailor: 'At the tailor',
  other: 'Maintenance, transit',
};
